import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react'

import { getActiveLanguage } from 'react-localize-redux'
import { connect } from 'react-redux'

import { getUser } from '../reducers/UserReducer'
import { isObject, recursiveIncludes } from '../utils'
import { getInstitution, getInstitutionEvaluationQuestions, getInstitutionOptions, getInstitutionRejectionReasons, getInstitutionSectors } from '../utils/api/institution'
import { InstitutionOptions, InstitutionRoles } from '../utils/constants'
import { fetchAllActTypes } from '../utils/api/actType'
import { colorShade, getLuminanceFromColorCode } from '../utils/colors'
import { ArrayColorsPalette } from '../config/colors'
import { GlobalContext } from './GlobalProvider'

const mapStateToProps = state => ({
  currentLanguage: getActiveLanguage(state.locale).code,
  user: getUser(state.getUser)
})

export const InstitutionContext = createContext()

const InstitutionProvider = ({ children, currentLanguage, user }) => {
  const { sections, sectionToYears, setModules } = useContext(GlobalContext)

  const [institution, setInstitution] = useState(null)
  const [institutionGroup, setInstitutionGroup] = useState(null) // The group for which the current institution is a part
  const [selectedInstitution, setSelectedInstitution] = useState(null)
  const [schools, setSchools] = useState([])
  const [users, setUsers] = useState([])
  const [rawSectors, setRawSectors] = useState([])
  const [actTypes, setActTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [evaluationQuestions, setEvaluationQuestions] = useState([])
  const [institutionOptions, setInstitutionOptions] = useState([])
  const [rejectionReasons, setRejectionReasons] = useState([])

  const options = useMemo(() => institutionOptions.reduce((acc, io) => {
    acc[io.optionType.type] = io.optionEnabled
    return acc
  }, {}), [institutionOptions])

  const useRecruitmentModule = useMemo(() => options[InstitutionOptions.RECRUITMENT_MODULE] ?? false, [options])
  const useTrainingGroundValidation = useMemo(() => options[InstitutionOptions.INTERNSHIP_REQUEST_APPROVAL_BY_SECTOR_ADMIN] ?? false, [options])

  const coloredSchools = useMemo(() => schools.map((school, index) => {
    const color = ArrayColorsPalette[index % ArrayColorsPalette.length].value

    const backgroundColor = color
    const borderColor = colorShade(color, -40)

    school.color = {
      border: borderColor,
      background: backgroundColor,
      text: getLuminanceFromColorCode(color)
    }

    return school
  }), [schools])

  const sectionsColorMap = useMemo(() => sections.reduce((acc, section, index) => {
    const color = ArrayColorsPalette[index % ArrayColorsPalette.length].value
    const backgroundColor = color
    const borderColor = colorShade(color, -40)

    acc[section.id] = {
      border: borderColor,
      background: backgroundColor,
      text: getLuminanceFromColorCode(color)
    }

    return acc
  }, {}), [sections])

  const coloredSectionToYears = useMemo(() => sectionToYears.map((sty, index) => {
    if (isObject(sty.section)) {
      sty.color = sectionsColorMap[sty.section.id]
    }

    return sty
  }), [sectionToYears, sectionsColorMap])

  const sectors = useMemo(() => {
    const sectors = {
      active: [],
      archived: []
    }

    rawSectors.forEach(s => {
      if (s.archived) {
        sectors.archived.push(s)
      } else {
        sectors.active.push(s)
      }
    })

    return sectors
  }, [rawSectors])
  const sectorsOrderByName = useMemo(() => {
    return sectors.active.sort((a, b) => a.name.localeCompare(b.name))
  }, [sectors.active])

  const fetchInstitution = useCallback(() => {
    getInstitution(user, user.institutions[0]).then(json => {
      if (json?.data) {
        setInstitution(json.data)
        setInstitutionGroup(json.data.institutionGroup)
        setSchools(json.data.schools ?? [])
        setUsers(json.data.users ?? [])
      }

      setLoading(false)
    })
  }, [user.id, user.roles, setInstitution, setInstitutionGroup, setLoading, setSchools])

  const fetchActTypes = useCallback(() => {
    fetchAllActTypes(user, { orderBy: 'acronym', contexts: ['section'] }).then(json => {
      if (json?.data) {
        setActTypes(json.data)
      }
    })
  }, [user.id])

  const fetchSectors = useCallback(() => {
    if (institution) {
      getInstitutionSectors(user, institution, { strictMode: 1, orderBy: 'name' }).then(json => {
        if (json?.data) {
          setRawSectors(json.data)
        }
      })
    }
  }, [user.id, institution?.id])

  const fetchEvaluationQuestions = useCallback(() => {
    if (institution) {
      getInstitutionEvaluationQuestions(user, institution).then(json => {
        if (json?.data) {
          setEvaluationQuestions(json.data)
        }
      })
    }
  }, [user.id, institution?.id])

  const fetchOptions = useCallback(() => {
    if (institution) {
      getInstitutionOptions(user, institution).then(({ data }) => setInstitutionOptions(data))
    }
  }, [user.id, institution?.id])

  const fetchRejectionReasons = useCallback(() => {
    if (institution) {
      getInstitutionRejectionReasons(user, institution).then(({ data }) => setRejectionReasons(data))
    }
  }, [user.id, institution?.id])

  const updateOption = useCallback(newOptions => {
    setInstitutionOptions(options => options.map(o => {
      const existingOption = newOptions.find(no => no.id === o.id)

      return isObject(existingOption) ? existingOption : o
    }))
  }, [])

  useEffect(() => {
    if (user && recursiveIncludes(user.roles, [InstitutionRoles.OBSERVER, InstitutionRoles.NURSE, InstitutionRoles.HOSPITAL_ADMIN])) {
      setLoading(true)
      fetchInstitution()
    } else {
      setLoading(false)
    }
  }, [user.roles, fetchInstitution, setLoading])

  useEffect(() => {
    fetchSectors()
  }, [fetchSectors])

  useEffect(() => {
    fetchEvaluationQuestions()
  }, [fetchEvaluationQuestions])

  useEffect(() => fetchOptions(), [fetchOptions])

  useEffect(() => {
    fetchRejectionReasons()
  }, [fetchRejectionReasons])

  useEffect(() => {
    const newModules = { [InstitutionOptions.RECRUITMENT_MODULE]: false, [InstitutionOptions.MODULE_SETTINGS]: false }

    institutionOptions.forEach(o => {
      if (o.optionType.type === InstitutionOptions.RECRUITMENT_MODULE) {
        newModules[InstitutionOptions.RECRUITMENT_MODULE] = o.optionEnabled
      } else if (o.optionType.type === InstitutionOptions.MODULE_SETTINGS) {
        newModules[InstitutionOptions.MODULE_SETTINGS] = o.optionEnabled
      }
    })

    setModules(oldModules => ({ ...oldModules, ...newModules }))
  }, [institutionOptions])

  return (
    <InstitutionContext.Provider
      value={{
        actTypes,
        archivedSectors: sectors.archived,
        evaluationQuestions,
        sectors: sectors.active,
        sectorsOrderByName,
        institution,
        institutionGroup,
        institutionOptions,
        loading,
        options,
        rejectionReasons,
        reload: fetchInstitution,
        reloadSectors: fetchSectors,
        loadActTypes: fetchActTypes,
        schools,
        coloredSchools,
        coloredSectionToYears,
        sectionsColorMap,
        useRecruitmentModule,
        useTrainingGroundValidation,
        users,
        selectedInstitution,
        setRejectionReasons,
        setSchools,
        setUsers,
        setSelectedInstitution,
        updateOption
      }}
    >
      {children}
    </InstitutionContext.Provider>
  )
}

export default connect(mapStateToProps)(InstitutionProvider)
